import { CircleCheck, ChevronLeft, CreditCard, Plus, Repeat, Undo2, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { logPageClickBi } from '../../../lib/bi-logger';
import { useTranslation } from '../../../lib/i18n';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { fromDayParam, toDayParam } from '../../../lib/calendar/calendar-dates';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { executeCardAction, OUTCOME_UNKNOWN, prepareCalendarAction } from '../../../lib/card-actions';
import { conversationCreateBody } from '../../../lib/conversation-space';
import { queryClient, queryKeys } from '../../../lib/query-client';
import {
  bookCreate,
  bookedItem,
  bookedLine,
  bookedMessages,
  bookedStaffName,
  bookRefusalKey,
  bookableSites,
  bookingStep,
  bookTitle,
  clientName,
  newBookingDraft,
  newClientReady,
  nextServiceStart,
  notifiedLine,
  servicePrice,
  serviceStaff,
  type BookedMessageKind,
  type BookingClient,
  type BookingDraft,
  type BookingService,
  type MadeBooking,
  type OpenBookingCard,
} from '../../../lib/calendar/booking-card';
import type { CalendarItem } from '../../../lib/calendar/calendar-item';
import { fetchCardBooking } from '../../../lib/calendar/use-live-card-booking';
import { useBookingContacts, useBookingServices, useBookingSlots } from '../../../lib/calendar/use-booking-card-reads';
import { useDebounced } from '../../../lib/search';
import { useAgentName } from '../../../lib/use-agent-name';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Avatar, Button, Card, IconButton, Input, Switch, Typography } from '../ui';
import { BookingConfirm, useBookingActions } from './BookingActions';
import { useCalendarFormat } from './use-calendar-format';
import styles from './BookingCard.module.css';

// The Calendar's booking card, drawn at the end of the chat. Nothing here reaches Maestro as a turn: each step only reads
// (the site's contacts and services), and Book makes the booking through the Calendar's own booking write, after which
// the card says it is booked and the harness notes it in the Calendar chat for Maestro. A refused Book leaves the card
// open with the reason.
/** A new, empty Maestro chat (no turn runs), for a card that was in a fresh chat; null when it cannot be made. */
async function createBookingChat(): Promise<string | null> {
  try {
    const res = await apiFetch(`${API_BASE}/api/conversations`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(conversationCreateBody(undefined)),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { conversation?: { id?: string } };
    return data.conversation?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * How the booked card's message chips reach Maestro: the chat drawing the card sends them as the owner's own message,
 * and says whether it can take one now (`bookedCardCanAsk`). `send` resolves whether the message was sent.
 */
export interface BookedCardAsk {
  canAsk: boolean;
  send: (message: string) => Promise<boolean>;
}

export function BookingCard({ card, ask }: { card: OpenBookingCard; ask: BookedCardAsk }) {
  const { t } = useTranslation();
  const { closeBookingCard, markBookingCardBooked, adoptBookingChat } = useActiveConversation();
  const format = useCalendarFormat(card.start.timeZone);
  const sites = useMemo(() => bookableSites(card.start.sites), [card.start.sites]);
  const [draft, setDraft] = useState<BookingDraft>(() => newBookingDraft(card.start, card.requestId));
  const step = bookingStep(draft);
  const site = sites.find((each) => each.siteId === draft.siteId);
  const update = (change: Partial<BookingDraft>) => setDraft((current) => ({ ...current, ...change }));
  const click = (name: string) => logPageClickBi({ page_name: 'chat', button_name: `calendar_book_${name}` });

  const back = () => {
    click('back');
    if (step === 'when') update({ service: undefined });
    else if (step === 'service') update({ client: undefined });
    else if (step === 'client' && sites.length > 1) update({ siteId: undefined });
  };
  const canGoBack = step === 'when' || step === 'service' || (step === 'client' && sites.length > 1);

  // Book makes a booking only at an exact open time, so the time step offers that day's open times and nothing else.
  const slots = useBookingSlots({
    siteId: draft.siteId,
    serviceId: draft.service?.id,
    staffResourceId: draft.staffResourceId,
    day: toDayParam(draft.start, card.start.timeZone),
    timeZone: card.start.timeZone,
  });
  const slotOpen = (slots.data ?? []).some((slot) => slot.start === draft.start);
  const [booking, setBooking] = useState(false);
  const madeChat = useRef<string | null>(null);
  const [refusal, setRefusal] = useState<ReturnType<typeof bookRefusalKey> | null>(null);
  // A new time or staff member is a new try, so the last refusal no longer describes it.
  useEffect(() => setRefusal(null), [draft]);
  const book = async () => {
    const create = bookCreate(draft);
    if (!create || !draft.siteId || !draft.service || !draft.client || booking) return;
    click('book');
    setBooking(true);
    setRefusal(null);
    const prepared = await prepareCalendarAction({ siteId: draft.siteId, op: 'bookings.create', record: draft.requestId, create });
    // The harness notes the booking for Maestro in the card's chat, so a card in a fresh chat first makes that chat, and it
    // becomes the card's and the Calendar chat only once the booking is made; a retry reuses it, and a chat that cannot be
    // made only costs the note.
    if (!prepared.refused && !card.conversationId && !madeChat.current) madeChat.current = await createBookingChat();
    const conversationId = card.conversationId ?? madeChat.current;
    const done = prepared.refused
      ? undefined
      : await executeCardAction(prepared.token, {
          skipCustomerNotification: !draft.notifyClient,
          ...(conversationId ? { calendarConversationId: conversationId } : {}),
        });
    setBooking(false);
    // The harness notes a made booking, and one Wix may hold after an unanswered confirm, in the chat the run named.
    const noted = done?.ok || done?.reason === OUTCOME_UNKNOWN;
    if (noted && !card.conversationId && conversationId) adoptBookingChat(card.requestId, conversationId);
    if (!done?.ok) {
      setRefusal(bookRefusalKey(done?.reason ?? prepared.reason));
      return;
    }
    // Made, and the card says so in place: Maestro is noted in the Calendar chat by the harness, with no turn and no reply.
    // No reply arrives to refresh the Calendar either, so it reads again now to show the new booking.
    void queryClient.invalidateQueries({ queryKey: queryKeys.calendar, refetchType: 'active' });
    markBookingCardBooked(draft.requestId, { draft, bookingId: done.createdId });
  };

  const close = () => {
    click('close');
    closeBookingCard(card.requestId);
  };

  if (card.booked) return <BookedCard card={card} made={card.booked} format={format} ask={ask} onClose={close} />;

  return (
    <Card variant="outline" size="sm" className={styles.card} data-testid="calendarBookingCard">
      <div className={styles.head}>
        {canGoBack && (
          <IconButton size="tiny" aria-label={t('common.back')} onClick={back}>
            <ChevronLeft size={16} aria-hidden className="dir-flip" />
          </IconButton>
        )}
        <Typography variant="h4" className={styles.title}>
          {t('calendar.book.title')}
        </Typography>
        <IconButton size="tiny" aria-label={t('common.close')} onClick={close}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>

      {step === 'site' && (
        <Step question={t('calendar.book.site')}>
          {sites.map((each) => (
            <Button key={each.siteId} variant="outlined" size="small" fullWidth onClick={() => update({ siteId: each.siteId })}>
              {each.name ?? each.siteId}
            </Button>
          ))}
        </Step>
      )}

      {step === 'client' && draft.siteId && (
        <ClientStep siteId={draft.siteId} onPick={(client) => update({ client })} />
      )}

      {step === 'service' && draft.siteId && (
        <ServiceStep
          siteId={draft.siteId}
          format={format}
          onPick={(service) => {
            const staff = serviceStaff(site, service, draft.staffResourceId);
            update({ service, staffResourceId: staff[0]?.resourceId });
          }}
        />
      )}

      {step === 'when' && draft.client && draft.service && (
        <Step question={t('calendar.book.when')}>
          <Typography variant="p2" className={styles.summary}>
            {[clientName(draft.client), draft.service.name, t('meetings.duration.minutes', { count: draft.service.minutes })].join(' · ')}
          </Typography>
          <div className={styles.choices} role="radiogroup" aria-label={t('calendar.details.staff')}>
            {serviceStaff(site, draft.service, draft.staffResourceId).map((member) => (
              <Button
                key={member.resourceId}
                role="radio"
                aria-checked={member.resourceId === draft.staffResourceId}
                variant={member.resourceId === draft.staffResourceId ? 'contained' : 'outlined'}
                size="small"
                onClick={() => update({ staffResourceId: member.resourceId })}
              >
                {member.name}
              </Button>
            ))}
          </div>
          <Input
            type="date"
            aria-label={t('calendar.book.day')}
            value={toDayParam(draft.start, card.start.timeZone)}
            onChange={(event) => {
              const day = fromDayParam(event.target.value, card.start.timeZone);
              if (day !== undefined) update({ start: day });
            }}
          />
          <Rows loading={slots.isInitialLoading} failed={slots.isError} onRetry={() => void slots.refetch()} empty={t('calendar.book.noSlots')}>
            {(slots.data ?? []).length === 0
              ? []
              : [
                  <div key="slots" className={styles.choices} role="radiogroup" aria-label={t('calendar.book.day')}>
                    {(slots.data ?? []).map((slot) => (
                      <Button
                        key={slot.start}
                        role="radio"
                        aria-checked={slot.start === draft.start}
                        variant={slot.start === draft.start ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => update({ start: slot.start })}
                      >
                        {format.time(slot.start)}
                      </Button>
                    ))}
                  </div>,
                ]}
          </Rows>
          <Switch checked={draft.notifyClient} onChange={(notifyClient) => update({ notifyClient })} label={t('calendar.book.notify')} />
          {refusal && (
            <Typography variant="p3" role="alert" className={styles.refusal}>
              {t(refusal, { staff: site?.staff.find((member) => member.resourceId === draft.staffResourceId)?.name ?? '' })}
            </Typography>
          )}
          <Button fullWidth loading={booking} disabled={!bookCreate(draft) || !slotOpen} onClick={() => void book()}>
            {bookTitle(draft.service, t, format.parts)}
          </Button>
        </Step>
      )}
    </Card>
  );
}

/**
 * The card once Book has made the booking: what was booked, whether the client was told, and the next steps. Ask for
 * payment and Repeat weekly are the owner's own messages to Maestro in the card's own chat, where the booking is noted,
 * each sent once and offered only when that chat exists; Add another service opens a new card for the same client; Undo
 * is the details card's own Cancel for the new booking.
 */
function BookedCard({ card, made, format, ask, onClose }: {
  card: OpenBookingCard;
  made: MadeBooking;
  format: ReturnType<typeof useCalendarFormat>;
  ask: BookedCardAsk;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const agentName = useAgentName();
  const { startBookingCard, markBookingCardBooked } = useActiveConversation();
  const { draft } = made;
  const click = (name: string) => logPageClickBi({ page_name: 'chat', button_name: `calendar_book_${name}` });
  const site = card.start.sites.find((each) => each.siteId === draft.siteId);
  const staff = bookedStaffName(card.start.sites, made);
  const messages = card.conversationId ? bookedMessages(made, staff, t, format.parts) : {};

  const [item] = useState(() => bookedItem(made, staff, site?.name));
  const [failure, setFailure] = useState<string>();
  const undo = useBookingActions(item ?? NO_BOOKING, (notice) => {
    if (notice.severity === 'error') return setFailure(notice.message);
    void queryClient.invalidateQueries({ queryKey: queryKeys.calendar, refetchType: 'active' });
    markBookingCardBooked(card.requestId, { ...made, undone: true });
  });
  const canUndo = !!item && undo.buttons.some((button) => button.op === 'bookings.cancel');

  const [readingClient, setReadingClient] = useState(false);
  const addService = async () => {
    if (readingClient) return;
    let read: CalendarItem | undefined;
    if (draft.client?.kind === 'new' && draft.siteId) {
      setReadingClient(true);
      read = (await fetchCardBooking(draft.siteId, made.bookingId).catch(() => null)) ?? undefined;
      setReadingClient(false);
    }
    startBookingCard(nextServiceStart(card, made, read));
  };

  const busy = !!undo.preparing || undo.executing;
  // Recorded as sent as the send starts, so a second tap cannot send it again, and given back if it was not sent. The
  // latch holds a second tap that lands before the card is drawn again.
  const sending = useRef(new Set<BookedMessageKind>());
  const send = async (kind: BookedMessageKind) => {
    const message = messages[kind];
    if (!message || !ask.canAsk || sending.current.has(kind)) return;
    sending.current.add(kind);
    markBookingCardBooked(card.requestId, (current) => ({ ...current, sent: [...(current.sent ?? []), kind] }));
    const sent = await ask.send(message);
    sending.current.delete(kind);
    if (sent) return;
    markBookingCardBooked(card.requestId, (current) => ({ ...current, sent: (current.sent ?? []).filter((each) => each !== kind) }));
  };
  const followUps = !made.undone
    ? [
        ...(messages.askPayment
          ? [{ name: 'pay', label: t('calendar.book.askPayment'), icon: CreditCard, onClick: () => void send('askPayment') }]
          : []),
        ...(messages.repeatWeekly
          ? [{ name: 'repeat', label: t('calendar.book.repeatWeekly'), icon: Repeat, onClick: () => void send('repeatWeekly') }]
          : []),
        { name: 'another', label: t('calendar.book.addService'), icon: Plus, onClick: () => void addService() },
      ]
    : [];
  const notified = made.undone ? undefined : notifiedLine(made, t);

  return (
    <Card variant="outline" size="sm" className={styles.card} data-testid="calendarBookingCard">
      <div className={styles.head}>
        <CircleCheck size={16} aria-hidden />
        <Typography variant="p2" role="status" className={styles.title}>
          {made.undone ? t('calendar.book.undone') : bookedLine(made, staff, t, format.parts)}
        </Typography>
        <IconButton size="tiny" aria-label={t('common.close')} onClick={onClose}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>
      {notified && (
        <Typography variant="p3" color="secondary">
          {notified}
        </Typography>
      )}
      {undo.confirm ? (
        <BookingConfirm
          key={undo.confirm.prepared.token}
          prepared={undo.confirm.prepared}
          op={undo.confirm.op}
          // The client hears of the cancel only if they heard of the booking.
          initialNotify={undo.confirm.notify ?? draft.notifyClient}
          initialMessage={undo.confirm.message}
          onAnswer={(confirmed, notify, message) => void undo.answer(confirmed, notify, message)}
        />
      ) : (
        !made.undone && (
          <div className={styles.choices}>
            {followUps.map(({ name, label, icon: Icon, onClick }) => (
              <Button key={name} variant="outlined" size="small" startIcon={<Icon size={16} aria-hidden />} disabled={busy || (name === 'another' ? readingClient : !ask.canAsk)}
                loading={name === 'another' && readingClient} onClick={() => {
                  click(name);
                  onClick();
                }}>
                {label}
              </Button>
            ))}
            {canUndo && (
              <Button variant="outlined" size="small" startIcon={<Undo2 size={16} aria-hidden className="dir-flip" />} loading={undo.preparing === 'bookings.cancel'}
                disabled={busy} onClick={() => {
                  click('undo');
                  setFailure(undefined);
                  void undo.prepare('bookings.cancel');
                }}>
                {t('cardAction.undo')}
              </Button>
            )}
          </div>
        )
      )}
      {(failure || undo.failed) && (
        <Typography variant="p3" role="alert" className={styles.refusal}>
          {failure ?? t('calendar.details.refused', { maestroAgentName: agentName.name })}
        </Typography>
      )}
    </Card>
  );
}

/** The hook's stand-in when Book's reply named no booking: it offers no buttons, so there is nothing to undo. */
const NO_BOOKING: CalendarItem = { key: 'booking:none', kind: 'booking', title: '', start: 0, end: 0, allDay: false, ref: {} };

function Step({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <div className={styles.step}>
      <Typography variant="p1" className={styles.question}>
        {question}
      </Typography>
      {children}
    </div>
  );
}

function ClientStep({ siteId, onPick }: { siteId: string; onPick: (client: BookingClient) => void }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const query = useDebounced(search.trim(), 250);
  const contacts = useBookingContacts(siteId, query);
  const [adding, setAdding] = useState<Extract<BookingClient, { kind: 'new' }> | null>(null);

  if (adding) {
    const field = (key: 'firstName' | 'lastName' | 'email' | 'phone') => ({
      value: adding[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => setAdding({ ...adding, [key]: event.target.value }),
    });
    return (
      <Step question={t('calendar.book.newClient')}>
        <Input label={t('common.name')} autoComplete="off" {...field('firstName')} />
        <Input label={t('contacts.channel.email')} type="email" autoComplete="off" {...field('email')} />
        <Input label={t('connectors.app.phone')} type="tel" autoComplete="off" {...field('phone')} />
        <Button fullWidth disabled={!newClientReady(adding)} onClick={() => onPick(splitName(adding))}>
          {t('calendar.book.continue')}
        </Button>
      </Step>
    );
  }
  return (
    <Step question={t('calendar.book.client')}>
      <Input kind="search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} onClear={() => setSearch('')}
        placeholder={t('common.search')} />
      <Button variant="text" size="small" startIcon={<UserPlus size={16} aria-hidden />}
        onClick={() => setAdding({ kind: 'new', firstName: search.trim(), lastName: '', email: '', phone: '' })}>
        {t('calendar.book.newClient')}
      </Button>
      <Rows loading={contacts.isInitialLoading} failed={contacts.isError} onRetry={() => void contacts.refetch()} empty={t('calendar.book.noClients')}>
        {(contacts.data ?? []).map((contact) => {
          const name = clientName({ kind: 'contact', contact });
          return (
            <Card key={contact.id} variant="stone" size="sm" interactive component="button" className={styles.row}
              onClick={() => onPick({ kind: 'contact', contact })}>
              <Avatar initials={avatarInitials(name)} size={24} color={avatarSlotFor(name)} />
              <span className={styles.rowText}>
                <span dir="auto" className="sym-type-small-medium">{name}</span>
                {contact.email && name !== contact.email && <span className="sym-p3">{contact.email}</span>}
              </span>
            </Card>
          );
        })}
      </Rows>
    </Step>
  );
}

/** The new client's one name field: its first word is the first name, as Wix keeps first and last apart. */
function splitName(client: Extract<BookingClient, { kind: 'new' }>): BookingClient {
  const [first, ...rest] = client.firstName.trim().split(/\s+/);
  return { ...client, firstName: first ?? '', lastName: rest.join(' ') };
}

function ServiceStep({ siteId, format, onPick }: { siteId: string; format: ReturnType<typeof useCalendarFormat>; onPick: (service: BookingService) => void }) {
  const { t } = useTranslation();
  const services = useBookingServices(siteId);
  return (
    <Step question={t('calendar.book.service')}>
      <Rows loading={services.isInitialLoading} failed={services.isError} onRetry={() => void services.refetch()} empty={t('calendar.book.noServices')}>
        {(services.data ?? []).map((service) => (
          <Card key={service.id} variant="stone" size="sm" interactive component="button" className={styles.row} onClick={() => onPick(service)}>
            <span className={styles.rowText}>
              <span dir="auto" className="sym-type-small-medium">{service.name}</span>
              <span className="sym-p3">
                {[t('meetings.duration.minutes', { count: service.minutes }), ...(service.price ? [servicePrice(service, format.parts)] : [])].join(' · ')}
              </span>
            </span>
          </Card>
        ))}
      </Rows>
    </Step>
  );
}

/** Rows of a title bar over a shorter line, at uneven widths, the shape the rows land in. */
const SKELETON_WIDTHS = [[62, 40], [78, 52], [50, 34]] as const;

function Rows({ loading, failed, onRetry, empty, children }: { loading: boolean; failed: boolean; onRetry: () => void; empty: string; children: React.ReactNode[] }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className={styles.rows} aria-busy="true" aria-label={t('common.loading')}>
        {SKELETON_WIDTHS.map(([title, line], index) => (
          <div key={index} className={styles.skeletonRow}>
            <span className={`orion-shimmer ${styles.skeletonBar}`} style={{ width: `${title}%` }} />
            <span className={`orion-shimmer ${styles.skeletonLine}`} style={{ width: `${line}%` }} />
          </div>
        ))}
      </div>
    );
  }
  if (failed) {
    return (
      <div className={styles.failed}>
        <Typography variant="p3" color="secondary">{t('common.error.generic')}</Typography>
        <Button variant="outlined" size="small" onClick={onRetry}>{t('common.retry')}</Button>
      </div>
    );
  }
  if (children.length === 0) return <Typography variant="p3" color="secondary">{empty}</Typography>;
  return <div className={styles.rows}>{children}</div>;
}
